import { Theme, WithStyles, withStyles } from "@material-ui/core/styles";
import { CSSProperties } from "@material-ui/core/styles/withStyles";
import React from "react";
import {
  IQuestionRequest,
  ISanitizedSurveyQuestion,
  TemplateQuestionType,
} from "src/services";
import Component from "../../../components/Component";
import "./SurveyQuestions.css";

const styles = (theme: Theme): Record<string, CSSProperties> => ({
  textArea: {
    border: "none",
    fontSize: "14px",
    padding: "0",
    resize: "none",
    width: "100%",
  },
});

interface IQuestionProps {
  question: ISanitizedSurveyQuestion;
  categoryIndex: number;
  index: number;
  isAdSurvey?: boolean;
  hideWrittenResponseGuidance?: boolean;
  // TODO: You're building a new handler every render. That is wrong.
  handleResponseChange: (
    i: number
  ) => (e: React.ChangeEvent<HTMLElement | { value: string }>) => void;
  questionResponse: IQuestionRequest;
}

type IProps = IQuestionProps & WithStyles<keyof ReturnType<typeof styles>>;

/** Keep English on the first line and Spanish on the next line. */
function renderBilingualQuestionText(text: string) {
  const normalized = String(text || "").replace(/\r\n/g, "\n").trim();
  if (!normalized) {
    return null;
  }

  const newlineParts = normalized
    .split(/\n+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  if (newlineParts.length >= 2) {
    return (
      <>
        <span>{newlineParts[0]}</span>
        <span style={{ display: "block", height: 8 }} />
        <span>{newlineParts.slice(1).join(" ")}</span>
      </>
    );
  }

  // English sentence ends, then Spanish translation begins (e.g. "...coach. Mi ...").
  const bilingualMatch = normalized.match(
    /^(.*?\.)\s+((?:Mi|Mis|Cuando|Nuestro|Nuestra|Nuestros|Nuestras|Yo|Me|Nos|Si|En|Para|Por|Con|Sin|Una|Un|Este|Esta|Estos|Estas|Que|Qué|El|La|Los|Las|Su|Sus|¿)[\s\S]+)$/
  );
  if (bilingualMatch) {
    return (
      <>
        <span>{bilingualMatch[1]}</span>
        <span style={{ display: "block", height: 8 }} />
        <span>{bilingualMatch[2]}</span>
      </>
    );
  }

  return normalized;
}

export default withStyles(styles)(
  class Question extends Component<IProps, {}> {
    public render() {
      const {
        question,
        index,
        questionResponse,
        classes,
        categoryIndex,
        isAdSurvey,
        hideWrittenResponseGuidance,
      } = this.props;

      if (questionResponse === undefined) {
        return <div>Something is wrong</div>;
      }

      if (question.type === TemplateQuestionType.scale) {
        const scale = question.scaleScale;
        const value = questionResponse.response;
        const selectedValue = value !== "" ? parseInt(value, 10) : -1;
        const scaleValues = Array.from({ length: scale }).map((_, i) => i);
        const displayedScaleValues = question.scaleReversed
          ? scaleValues.slice().reverse()
          : scaleValues;
        const responseLabels = [
          "Strongly Disagree",
          "Disagree",
          "Slightly Disagree",
          "Average",
          "Slightly Agree",
          "Agree",
          "Strongly Agree",
        ];
        const sentimentMap = [
          { class: "scale__sentiment--red", emoji: "☹️", text: "Strongly disagree" },
          { class: "scale__sentiment--orange-red", emoji: "🙁", text: "Disagree" },
          { class: "scale__sentiment--orange", emoji: "😐", text: "Slightly disagree" },
          { class: "scale__sentiment--yellow", emoji: "😑", text: "Average" },
          { class: "scale__sentiment--green-mid", emoji: "🙂", text: "Slightly agree" },
          { class: "scale__sentiment--green-bright", emoji: "😊", text: "Agree" },
          { class: "scale__sentiment--dark-green", emoji: "😄", text: "Strongly agree" },
        ];
        const btnColorClasses = [
          "scale__btn--red",
          "scale__btn--orange-red",
          "scale__btn--orange",
          "scale__btn--yellow",
          "scale__btn--green-mid",
          "scale__btn--green-bright",
          "scale__btn--dark-green",
        ];
        const showInitialAverageCue =
          categoryIndex === 0 && selectedValue === -1;
        return (
          <section
            className={`question-card ${questionResponse.response ? "question-card--answered" : ""}`}
            aria-labelledby={`q${index + 1}-label`}
          >
            <div className="question-card__header">
              <span
                className="question-card__number"
                aria-label={`Question ${categoryIndex + 1}`}
              >
                {categoryIndex + 1}
              </span>
              <div>
              <p className="question-card__text" id={`q${index + 1}-label`}>
                {renderBilingualQuestionText(question.text)}
              </p>
              </div>
              {questionResponse.response ? (
                <span
                  className={[
                    "question-card__check",
                    "question-card__check--done",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  aria-label="Answered"
                >
                  {selectedValue >= 0 && sentimentMap[selectedValue]
                    ? sentimentMap[selectedValue].emoji
                    : ""}
                </span>
              ) : null}
            </div>
            <div className="scale scale-space" role="group" aria-label={`Scale for question ${index + 1}`}>
              <div className="scale__buttons">
                {displayedScaleValues.map((scaleValue) => {
                  const isSelected = selectedValue === scaleValue;
                  const responseLabel =
                    responseLabels[scaleValue] || `${scaleValue + 1}`;
                  const btnClass = [
                    "scale__btn",
                    isSelected ? "scale__btn--selected" : "",
                    btnColorClasses[scaleValue],
                  ]
                    .filter(Boolean)
                    .join(" ");
                  const buttonStyle = undefined;

                  return (
                    <label
                      className={btnClass}
                      title={`${scaleValue + 1} - ${responseLabel}`}
                      key={scaleValue}
                      style={buttonStyle}
                    >
                      <input
                        type="radio"
                        name={`q${index + 1}`}
                        value={scaleValue.toString()}
                        checked={isSelected}
                        aria-label={`${scaleValue + 1} - ${responseLabel}`}
                        onChange={() =>
                          this.props.handleResponseChange(index)({
                            target: { value: scaleValue.toString() },
                          } as any)
                        }
                      />
                      <span>{responseLabel}</span>
                    </label>
                  );
                })}
              </div>
              {showInitialAverageCue && (
                <div className="scale__initial-cue" aria-hidden="true">
                  <span className="scale__initial-cue-label">Average</span>
                </div>
              )}
              {selectedValue >= 0 && sentimentMap[selectedValue] && (
                <div
                  className={[
                    "scale__selected-feedback",
                    sentimentMap[selectedValue].class,
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {`${sentimentMap[selectedValue].emoji} ${sentimentMap[selectedValue].text}`}
                </div>
              )}
            </div>
          </section>
        );
      }

      if (question.type === TemplateQuestionType.written) {
        const responseLength = questionResponse.response.length;

        return (
          <section
            className={`question-card question-card--written ${questionResponse.response ? "question-card--answered" : ""}`}
            aria-labelledby={`q${index + 1}-label`}
          >
            <div className="question-card__header">
              <span
                className="question-card__number"
                aria-label={`Question ${categoryIndex + 1}`}
              >
                {categoryIndex + 1}
              </span>
              <p className="question-card__text" id={`q${index + 1}-label`}>
                {renderBilingualQuestionText(question.text)}
              </p>
              {questionResponse.response ? (
                <span
                  className="question-card__check question-card__check--done"
                  aria-label="Answered"
                >
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <circle cx="9" cy="9" r="9" fill="#2d8f5e" />
                    <path
                      d="M5 9.5l3 3 5-6"
                      stroke="#fff"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </span>
              ) : null}
            </div>
            <div className="written-response">
              <textarea
                className={`${classes.textArea} written-response__textarea`}
                rows={4}
                maxLength={500}
                placeholder={hideWrittenResponseGuidance ? "" : "Share your ideas for building these values."}
                value={questionResponse.response}
                onChange={this.props.handleResponseChange(index)}
              />
              <div className="written-response__meta">
                {!hideWrittenResponseGuidance ? (
                  <span className="written-response__hint">
                    {isAdSurvey
                      ? "Your Athletic Director will see your responses."
                      : "Your response is confidential and will not be shared with the coach."}
                  </span>
                ) : null}
                <span className="written-response__count">{responseLength} / 500</span>
              </div>
            </div>
          </section>
        );
      }

      return <span>error. this should not happen</span>;
    }
  }
);



// WEBPACK FOOTER //
// ./src/pages/respondent/surveyRespondPage/Question.tsx